import ActionButtons from '@components/Viewer/UserInterface/ActionButtons/ActionButtons';
import ModalManager from '@components/Viewer/UserInterface/ModalManager';
import NavOverlay from '@components/Viewer/UserInterface/NavOverlay/NavOverlay';
import Viewer from '@components/Viewer/Viewer';
import { ROUTE_PATHS } from '@constants/routes';
import SPInspection from '@lib/spproject/SPInspection';
import SPProject from '@lib/spproject/SPProject';
import { useLazyGetInspectionFileQuery, useLazyGetInspectionsQuery } from '@store/api/inspectionEndpoints';
import { useLazyGetProjectFileQuery } from '@store/api/projectEndpoints';
import { useAppDispatch, useAppSelector, useLoading } from '@store/hooks';
import { loadProject, reset, setInspection, setSelectedPOI, setStepIndex } from '@store/reducers/viewerSlice';
import AppTheme from '@theme/theme';
import { useEffect, useId, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const ViewerPage = () => {
  const [getProjectFile] = useLazyGetProjectFileQuery();
  const [getInspections] = useLazyGetInspectionsQuery();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projectId, inspectionId, poiId, stepIndex } = useParams();
  const { project, inspection } = useAppSelector((state) => state.viewer);
  const authState = useAppSelector((state) => state.auth);
  const { show, hide } = useLoading();
  const [fetchInspection] = useLazyGetInspectionFileQuery();
  const [isInspectionLoading, setIsInspectionLoading] = useState<boolean>(false);
  const [isPOILoading, setIsPOILoading] = useState<boolean>(false);

  const componentId = useId();

  useEffect(() => {
    const isProjectNotReady = !project;
    const isNotReady = isProjectNotReady || isInspectionLoading || isPOILoading;

    (isNotReady ? show : hide)(componentId);
  }, [componentId, hide, isInspectionLoading, isPOILoading, project, show]);

  useEffect(() => {
    if (!authState.isAuthenticated) navigate(ROUTE_PATHS.LOGIN);
  }, [authState, navigate]);

  useEffect(() => {
    const initProject = async () => {
      if (!projectId) return;

      const projectInfo = await getProjectFile(projectId);
      const spproject = await SPProject.loadAsync(projectInfo.data!);
      const inspections = await getInspections({ projectId: spproject.manifest.projectInfo.id });

      spproject.setInspections(inspections.data?.map(inspection => inspection.info) ?? []);

      dispatch(loadProject({ project: spproject }));
    };

    initProject();

    return () => { dispatch(reset()); };
  }, [dispatch, getInspections, getProjectFile, projectId]);

  useEffect(() => {
    const initInspection = async () => {
      try {
        setIsInspectionLoading(true);

        if (!project || !inspectionId) throw null;
        const inspectionInfo = project?.inspections.find(inspection => inspection.id === inspectionId);

        if (!inspectionInfo) throw null;
        const projectId = project.manifest.projectInfo.id;
        const inspectionData = await fetchInspection({ projectId, inspectionId: inspectionInfo.id });

        if (!inspectionData.data) throw null;
        const spinspection = await SPInspection.loadAsync(inspectionData.data);

        dispatch(setInspection(spinspection));
      } catch (_) {
        dispatch(setInspection(null));
      } finally {
        setIsInspectionLoading(false);
      }
    };

    initInspection();

  }, [dispatch, fetchInspection, inspectionId, project, projectId]);

  useEffect(() => {
    const initPOI = async () => {
      try {
        setIsPOILoading(true);

        if (!project || !inspection || !poiId) throw null;
        const poi = inspection.inspection.pointOfInterests.find(poi => poi.id === poiId);

        if (!poi) throw null;

        dispatch(setSelectedPOI(poi));

        const sIndex = Number(stepIndex);
        dispatch(setStepIndex(isNaN(sIndex) ? 0 : sIndex));
      } catch (_) {
        dispatch(setSelectedPOI(null));
      } finally {
        setIsPOILoading(false);
      }
    };

    initPOI();

  }, [dispatch, inspection, poiId, project, stepIndex]);

  return project ? (
    <AppTheme disableCustomInputs={true}>
      <div style={{ backgroundColor: '#383838', width: '100vw', height: '100vh' }}>
        <NavOverlay />
        <Viewer />
        <ActionButtons />
        <ModalManager />
      </div>
    </AppTheme>
  ) : null;
};

export default ViewerPage;
